// Admin portal — shared types.
//
// Mirrors the retailer-portal's Page contract so admin pages look familiar.
// Differences:
//   - admin pages don't have per-tenant scoping (admin is god-mode)
//   - the chrome's PageContext exposes a `currentRetailerId` filter for
//     pages that work in either "all" or "single retailer" modes

import { api } from "@tkos/api-client";

export type ToastKind = "info" | "success" | "error";

export interface AdminUser {
  id: string;
  email: string;
  name: string;
}

export interface PageContext {
  api: typeof api;
  user: AdminUser;
  /** Programmatic navigation between admin pages. */
  navigate: (pageName: string) => void;
  /** Surface a toast in the chrome's toast region. */
  showToast: (msg: string, kind?: ToastKind) => void;
  /**
   * Optional "current retailer filter" — set by the Retailers page when an
   * admin selects a single retailer to drill into. Other pages can react.
   */
  getCurrentRetailerId: () => string | null;
  setCurrentRetailerId: (id: string | null) => void;
}

export type PageCleanup = () => void;

export interface Page {
  name: string;
  label: string;
  icon?: string;
  hidden?: boolean;
  mount(container: HTMLElement, ctx: PageContext): PageCleanup;
}

/** Address formatter — same defensive helper used by retailer + customer. */
export function formatAddress(addr: unknown): string {
  if (addr == null) return "—";
  if (typeof addr === "string") {
    const t = addr.trim();
    // Legacy poisoned string from earlier broken stringify — treat as empty.
    if (t === "" || t === "[object Object]") return "—";
    return t;
  }
  if (typeof addr !== "object") return "—";
  if (Array.isArray(addr)) return "—";
  const a = addr as Record<string, unknown>;
  const parts = (["line1", "line2", "city", "county", "postcode"] as const)
    .map((k) => a[k])
    .filter((v): v is string => typeof v === "string" && v.trim() !== "")
    .map((v) => v.trim());
  return parts.length > 0 ? parts.join(", ") : "—";
}
